import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { PestReport } from "@/components/PestReport";
import { Sprout, ShieldAlert, Eye, CalendarCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const riskBg = (r) => ({ high: "bg-red-600", medium: "bg-amber-500" }[(r || "").toLowerCase()] || "bg-emerald-600");

function RiskView({ data }) {
  const r = data.risk || {};
  return (
    <div className="space-y-5" data-testid="shared-risk">
      <div className="bg-[#78350F] text-white rounded-2xl p-6">
        <div className="text-xs uppercase tracking-wider opacity-70 font-semibold">Pest &amp; disease risk · {data.field_name}{data.location ? ` · ${data.location}` : ""}</div>
        <div className="flex items-center gap-3 mt-2">
          <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${riskBg(r.overall_risk)}`}>{r.overall_risk} risk</span>
          <span className="text-xs opacity-70">{r.generated_at ? new Date(r.generated_at).toLocaleDateString() : ""}</span>
        </div>
        <p className="mt-3 text-sm opacity-95">{r.summary}</p>
      </div>
      <ul className="space-y-2">
        {(r.warnings || []).map((w, i) => (
          <li key={i} className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-emerald-950 flex items-center gap-1.5 flex-wrap">{w.problem}{w.seen_before && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#78350F] text-white"><Eye className="h-3 w-3" /> seen nearby</span>}</div>
              <span className="text-[10px] font-bold uppercase text-stone-500">{w.risk}</span>
            </div>
            {w.crop && <div className="text-xs text-stone-500">{w.crop}</div>}
            <div className="text-sm text-stone-600 mt-1.5"><b>Why:</b> {w.trigger}</div>
            <div className="text-sm text-emerald-900 mt-1"><b>Do:</b> {w.action} <span className="text-stone-500">— {w.when}</span></div>
          </li>
        ))}
      </ul>
      {r.safe_windows?.length > 0 && (
        <div className="rounded-xl bg-[#EAF6EF] p-4">
          <div className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1 flex items-center gap-1"><CalendarCheck className="h-3.5 w-3.5" /> Good windows</div>
          <ul className="space-y-1">{r.safe_windows.map((t, i) => <li key={i} className="text-sm text-stone-700">• {t}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export default function SharedView() {
  const { token } = useParams();
  const [share, setShare] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/${token}`).then((r) => setShare(r.data)).catch((e) => setError(e.response?.data?.detail || "This link is not valid"));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-[#143D29] text-emerald-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg"><Sprout className="h-6 w-6 text-[#52B788]" /> AgroTerra</Link>
          <Link to="/register" data-testid="shared-cta" className="text-sm font-semibold bg-[#D97706] hover:bg-[#b45f05] text-white rounded-full px-4 py-2 transition-colors">Get free soil &amp; pest advice</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {error ? (
          <div data-testid="shared-error" className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-stone-600">{error}</div>
        ) : !share ? (
          <div className="flex items-center gap-2 text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <p data-testid="shared-by" className="text-sm text-stone-500 mb-4 flex items-center gap-1.5"><ShieldAlert className="h-4 w-4 text-[#78350F]" /> Shared by <b className="text-emerald-950">{share.shared_by?.name}</b>{share.shared_by?.farm_name ? ` · ${share.shared_by.farm_name}` : ""} on {new Date(share.created_at).toLocaleDateString()}</p>
            {share.kind === "pest_report" ? <PestReport report={{ ...share.data, id: null }} readOnly /> : <RiskView data={share.data} />}
          </>
        )}
      </main>
    </div>
  );
}
